import { ReactElement, ReactNode } from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { MultiSelect, MultiSelectFilterMode } from 'components/common/Multiselect';
import { OptionKeyAccessors } from 'components/common/Multiselect/types';
import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';

type MultiSelectFieldProps<
  T extends FieldValues,
  TOption = unknown,
> = FieldWrapperPassThroughProps &
  OptionKeyAccessors & {
    name: FieldPath<T>;
    control: Control<T>;
    placeholder?: string;
    /** Array of options - can be any data structure */
    options: TOption[];
    onRemove?: (value: string) => void;
    width?: string | number;
    maxVisibleChips?: number; // Show count when exceeded (default: 3)
    searchable?: boolean; // Enable search functionality
    searchPlaceholder?: string; // Placeholder for search input
    icon?: JSX.Element;
    filterMode?: MultiSelectFilterMode;
    onSearchChange?: (query: string) => void;
    searchHint?: ReactNode;
  };

/**
 * MultiSelectField component - A form field wrapper for MultiSelectInput using react-hook-form Controller
 * Easy to use with react-hook-form forms, handles validation and error states automatically
 *
 * @example
 * <MultiSelectField
 *   name="participants"
 *   control={control}
 *   options={users}
 *   valueKey="id"
 *   labelKey="name"
 *   secondaryKey="email"
 * />
 */
export const MultiSelectField = <T extends FieldValues, TOption = unknown>({
  name,
  control,
  label,
  error,
  placeholder = 'Select Names',
  options,
  onRemove,
  labelPosition,
  width,
  maxVisibleChips,
  searchable,
  searchPlaceholder,
  identifierKey = 'id',
  labelKey = 'label',
  secondaryKey,
  icon,
  filterMode,
  onSearchChange,
  searchHint,
}: MultiSelectFieldProps<T, TOption>): ReactElement => {
  return (
    <FieldWrapper label={label} error={error} labelPosition={labelPosition}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <MultiSelect
            placeholder={placeholder}
            options={options}
            selectedValues={value || []}
            onChange={onChange}
            onRemove={onRemove}
            width={width}
            maxVisibleChips={maxVisibleChips}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
            identifierKey={identifierKey}
            labelKey={labelKey}
            secondaryKey={secondaryKey}
            icon={icon}
            filterMode={filterMode}
            onSearchChange={onSearchChange}
            searchHint={searchHint}
          />
        )}
      />
    </FieldWrapper>
  );
};
